<template>
  <div class="manual-invoice-page">
    <div class="page-container">
      <!-- Stats Grid -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <!-- Total Invoices Card -->
        <div class="bg-card rounded-xl p-5 invoice-shadow animate-slide-up">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text w-5 h-5 text-accent-foreground">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                <path d="M10 9H8"></path>
                <path d="M16 13H8"></path>
                <path d="M16 17H8"></path>
              </svg>
            </div>
            <div>
              <p class="text-sm text-muted-foreground">Total Invoices</p>
              <p class="text-2xl font-bold text-foreground">{{ invoices.length }}</p>
            </div>
          </div>
        </div>

        <!-- Paid Card -->
        <div class="bg-card rounded-xl p-5 invoice-shadow animate-slide-up" style="animation-delay: 50ms;">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg bg-status-paid/15 flex items-center justify-center">
              <span class="text-status-paid font-bold">{{ paidCount }}</span>
            </div>
            <div>
              <p class="text-sm text-muted-foreground">Paid</p>
              <p class="text-lg font-semibold text-foreground">{{ pendingCount }} pending</p>
            </div>
          </div>
        </div>

        <!-- Total Amount Card -->
        <div class="bg-card rounded-xl p-5 invoice-shadow animate-slide-up" style="animation-delay: 100ms;">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
              <span class="text-primary-foreground font-bold text-xs">$</span>
            </div>
            <div>
              <p class="text-sm text-muted-foreground">Total Amount</p>
              <p class="text-2xl font-mono font-bold text-foreground">${{ totalAmount.toFixed(2) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Invoices Section -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-foreground">Recent Invoices</h2>
          <button @click="showForm = true" class="btn-new-invoice">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            New Invoice
          </button>
        </div>
        <!-- Loading State -->
        <div v-if="loadingInvoices" class="flex justify-center py-16">
          <font-awesome-icon icon="fa-solid fa-spinner" spin class="text-3xl text-muted-foreground" />
        </div>

        <!-- Empty State -->
        <div v-else-if="invoices.length === 0" class="flex flex-col items-center justify-center py-16 animate-fade-in">
          <div class="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text w-10 h-10 text-muted-foreground">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
              <path d="M10 9H8"></path>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-foreground mb-1">No invoices yet</h3>
          <p class="text-muted-foreground text-sm mb-4">Create your first invoice to get started</p>
          <button @click="showForm = true" class="btn-create-first">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Create Invoice
          </button>
        </div>

        <!-- Invoice List -->
        <div v-else class="invoices-table">
          <div v-for="invoice in invoices" :key="invoice.id" class="scan-row">
            <div class="scan-checkbox">
              <input type="checkbox" class="checkbox-input" />
            </div>
            
            <div class="scan-status status-completed">
              <span>✓ Completed</span>
            </div>
            
            <div class="scan-time">
              <LineIcon name="clock" :size="15" :stroke-width="1.8" style="width: 14px; height: 14px;" />
              <span>{{ getTimeAgo(invoice.created_at) }}</span>
            </div>
            
            <div class="scan-thumbnail">
              <img 
                v-if="invoice.file_path" 
                :src="`/api/invoices/${invoice.id}/original?thumbnail=true`" 
                :alt="invoice.result?.invoice_number"
                class="thumbnail-img"
              />
            </div>
            
            <div class="scan-info">
              <div class="invoice-header">
                <span class="invoice-type">ФАКТУРА</span>
                <span class="invoice-number">{{ invoice.result?.invoice_number || 'N/A' }}</span>
              </div>
              
              <div class="invoice-details">
                <LineIcon name="building" :size="15" :stroke-width="1.8" style="width: 14px; height: 14px;" />
                <span class="company-text">{{ invoice.result?.seller_name || 'N/A' }}</span>
              </div>
              
              <div class="invoice-amount">
                <span class="amount-value">{{ invoice.result?.grand_total || '0.00' }}</span>
                <span class="amount-currency">{{ invoice.result?.currency || 'BGN' }}</span>
              </div>
              
              <div class="invoice-date">
                <LineIcon name="calendar" :size="15" :stroke-width="1.8" style="width: 14px; height: 14px;" />
                <span>{{ formatDate(invoice.result?.invoice_date) }}</span>
              </div>
              
              <div class="invoice-type-selector">
                <SelectField v-model="invoice.type" :options="typeOptions" class="type-select" @change="updateInvoiceType(invoice)" />
              </div>
            </div>
            
            <div class="scan-actions">
              <button @click="goToInvoice(invoice.id)" class="btn-action btn-view" title="View Invoice">
                <LineIcon name="eye" :size="15" :stroke-width="1.8" />
                View
              </button>
              <button @click="exportInvoice(invoice.id)" class="btn-action btn-export" title="Export Invoice">
                <LineIcon name="download" :size="15" :stroke-width="1.8" />
                Export
              </button>
              <button @click="deleteInvoice(invoice.id)" class="btn-action btn-delete" title="Delete Invoice">
                <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Overlay -->
    <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
      <div class="modal-container">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header">
            <h2 class="modal-title">Create New Invoice</h2>
            <button @click="showForm = false" type="button" class="btn-close-modal">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body">
            <form @submit.prevent="handleSubmit" class="invoice-form">
              
              <!-- Your Company Section -->
              <div class="form-section">
                <h3 class="section-title">
                  <LineIcon name="building" :size="15" :stroke-width="1.8" />
                  Your Company
                </h3>
                
                <div class="form-row">
                  <div class="form-field">
                    <label>Company Name <span class="required">*</span></label>
                    <input
                      v-model="formData.seller_name"
                      type="text"
                      placeholder="Your Company Ltd."
                      required
                    />
                  </div>
                  
                  <div class="form-field">
                    <label>Company Logo</label>
                    <div class="upload-box">
                      <input type="file" id="company_logo" ref="logoInput" accept="image/png,image/jpeg,image/jpg" @change="handleLogoUpload" style="display: none;">
                      <button type="button" @click="$refs.logoInput.click()" class="upload-btn">
                        <LineIcon name="cloud-upload" :size="15" :stroke-width="1.8" />
                        Upload
                      </button>
                      <span class="upload-hint">PNG, JPG up to 2MB</span>
                    </div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-field">
                    <label>Invoice Number <span class="required">*</span></label>
                    <input
                      v-model="formData.invoice_number"
                      type="text"
                      placeholder="INV-520919"
                      required
                    />
                  </div>
                  
                  <div class="form-field">
                    <label>Invoice Type <span class="required">*</span></label>
                    <SelectField v-model="formData.type" :options="type2Options" />
                  </div>
                </div>
              </div>

              <!-- Client Information Section -->
              <div class="form-section">
                <h3 class="section-title">Client Information</h3>
                
                <div class="form-row">
                  <div class="form-field">
                    <label>Client Name</label>
                    <input
                      v-model="formData.buyer_name"
                      type="text"
                      placeholder="Acme Corporation"
                    />
                  </div>
                  
                  <div class="form-field">
                    <label>Client Email</label>
                    <input
                      v-model="formData.buyer_email"
                      type="email"
                      placeholder="client@example.com"
                    />
                  </div>
                </div>

                <div class="form-field">
                  <label>Client Address</label>
                  <textarea
                    v-model="formData.buyer_address"
                    rows="3"
                    placeholder="123 Business St, City, Country"
                  ></textarea>
                </div>
              </div>

              <!-- Dates Section -->
              <div class="form-row">
                <div class="form-field">
                  <label>Issue Date</label>
                  <DateField v-model="formData.invoice_date" />
                </div>
                
                <div class="form-field">
                  <label>Due Date</label>
                  <DateField v-model="formData.due_date" />
                </div>
              </div>

              <!-- Invoice Items Section -->
              <div class="form-section">
                <div class="section-header-with-button">
                  <h3 class="section-title">Invoice Items</h3>
                  <button type="button" @click="addItem" class="btn-add-item">
                    <LineIcon name="plus" :size="15" :stroke-width="1.8" />
                    Add Item
                  </button>
                </div>

                <div class="items-table">
                  <div class="table-header">
                    <div class="col-description">Description</div>
                    <div class="col-qty">Qty</div>
                    <div class="col-price">Unit Price ($)</div>
                    <div class="col-total">Total</div>
                    <div class="col-action"></div>
                  </div>

                  <div v-for="(item, index) in formData.items" :key="index" class="table-row">
                    <input
                      v-model="item.description"
                      type="text"
                      placeholder="Service or product description"
                      class="col-description"
                    />
                    <input
                      v-model.number="item.quantity"
                      type="number"
                      min="1"
                      class="col-qty"
                      @input="calculateItemTotal(index)"
                    />
                    <input
                      v-model.number="item.unit_price"
                      type="number"
                      step="0.01"
                      min="0"
                      class="col-price"
                      @input="calculateItemTotal(index)"
                    />
                    <div class="col-total">${{ (item.quantity * item.unit_price).toFixed(2) }}</div>
                    <button type="button" @click="removeItem(index)" class="btn-remove-item col-action">
                      <LineIcon name="trash" :size="15" :stroke-width="1.8" />
                    </button>
                  </div>
                </div>

                <div class="total-amount">
                  <span>Total Amount</span>
                  <strong>${{ itemsTotal.toFixed(2) }}</strong>
                </div>
              </div>

              <!-- Notes Section -->
              <div class="form-field">
                <label>Notes (Optional)</label>
                <textarea
                  v-model="formData.notes"
                  rows="3"
                  placeholder="Payment terms, thank you message, etc."
                ></textarea>
              </div>

              <!-- Form Actions -->
              <div class="form-actions">
                <button @click="showForm = false" type="button" class="btn btn-cancel">
                  Cancel
                </button>
                <button type="submit" class="btn btn-create" :disabled="loading">
                  <font-awesome-icon v-if="loading" icon="fa-solid fa-spinner" spin />
                  <span v-else>Create Invoice</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import DateField from '@/components/DateField.vue';
import SelectField from '@/components/SelectField.vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, reactive, computed, onMounted, unref } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/utils/api';

const router = useRouter();

const loading = ref(false);
const error = ref(null);
const logoPreview = ref(null);
const logoFile = ref(null);
const logoInput = ref(null);
const selectedCompany = ref(null);
const showForm = ref(false);
const invoices = ref([]);
const loadingInvoices = ref(true);

const formData = reactive({
  type: '',
  invoice_number: 'INV-' + Math.floor(100000 + Math.random() * 900000),
  invoice_date: new Date().toISOString().split('T')[0],
  due_date: '',
  currency: 'BGN',
  status: 'draft',
  seller_name: '',
  seller_tax_id: '',
  seller_address: '',
  buyer_name: '',
  buyer_email: '',
  buyer_tax_id: '',
  buyer_address: '',
  items: [
    { description: '', quantity: 1, unit_price: 0 }
  ],
  notes: '',
  subtotal: 0,
  tax_rate: 0,
  tax_amount: 0,
  grand_total: 0,
  notes: ''
});

const loadInvoices = async () => {
  loadingInvoices.value = true;
  try {
    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (selectedCompanyId) {
      const response = await api.get('/api/scans', {
        params: {
          company_id: selectedCompanyId,
          per_page: 20
        }
      });
      invoices.value = response.data.data || response.data;
    }
  } catch (err) {
    console.error('Failed to load invoices:', err);
  } finally {
    loadingInvoices.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getTimeAgo = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${diffDays}d`;
};

const goToInvoice = (id) => {
  router.push(`/scans/${id}`);
};

const updateInvoiceType = async (invoice) => {
  try {
    await api.patch(`/api/scans/${invoice.id}`, {
      type: invoice.type
    });
  } catch (err) {
    console.error('Failed to update invoice type:', err);
  }
};

const exportInvoice = (id) => {
  window.open(`/api/scans/${id}/download/pdf`, '_blank');
};

const deleteInvoice = async (id) => {
  if (!confirm('Are you sure you want to delete this invoice?')) return;
  
  try {
    await api.delete(`/api/scans/${id}`);
    await loadInvoices();
  } catch (err) {
    console.error('Failed to delete invoice:', err);
    alert('Failed to delete invoice');
  }
};

onMounted(async () => {
  // Load invoices
  await loadInvoices();

  // Load selected company from localStorage
  const selectedCompanyId = localStorage.getItem('selectedCompanyId');
  if (selectedCompanyId) {
    try {
      const response = await api.get(`/api/companies/${selectedCompanyId}`);
      selectedCompany.value = response.data;
      
      // Pre-fill seller data from selected company
      if (selectedCompany.value) {
        formData.seller_name = selectedCompany.value.name || '';
        formData.seller_tax_id = selectedCompany.value.vat_number || '';
        formData.seller_address = selectedCompany.value.address || '';
      }
    } catch (err) {
      console.error('Failed to load company:', err);
    }
  }
});

const totalAmount = computed(() => {
  return invoices.value.reduce((sum, invoice) => {
    const amount = parseFloat(invoice.result?.grand_total || 0);
    return sum + amount;
  }, 0);
});

const paidCount = computed(() => {
  return invoices.value.filter(inv => inv.status === 'paid').length;
});

const pendingCount = computed(() => {
  return invoices.value.filter(inv => inv.status !== 'paid').length;
});

const itemsTotal = computed(() => {
  return formData.items.reduce((sum, item) => {
    return sum + (item.quantity * item.unit_price);
  }, 0);
});

const addItem = () => {
  formData.items.push({ description: '', quantity: 1, unit_price: 0 });
};

const removeItem = (index) => {
  if (formData.items.length > 1) {
    formData.items.splice(index, 1);
  }
};

const calculateItemTotal = (index) => {
  const item = formData.items[index];
  item.total = item.quantity * item.unit_price;
};

const formatAmount = (amount) => {
  const val = parseFloat(amount) || 0;
  return val.toFixed(2);
};

const handleLogoUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (2MB max)
  if (file.size > 2 * 1024 * 1024) {
    error.value = 'Logo file size must be less than 2MB';
    return;
  }

  // Validate file type
  if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
    error.value = 'Logo must be PNG or JPG format';
    return;
  }

  logoFile.value = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    logoPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
  error.value = null;
};

const removeLogo = () => {
  logoPreview.value = null;
  logoFile.value = null;
  if (logoInput.value) {
    logoInput.value.value = '';
  }
};

const calculateTaxFromRate = () => {
  const subtotal = parseFloat(formData.subtotal) || 0;
  const taxRate = parseFloat(formData.tax_rate) || 0;
  formData.tax_amount = parseFloat((subtotal * (taxRate / 100)).toFixed(2));
  calculateTotal();
};

const calculateTotal = () => {
  const subtotal = parseFloat(formData.subtotal) || 0;
  const tax = parseFloat(formData.tax_amount) || 0;
  formData.grand_total = (subtotal + tax).toFixed(2);
};

const handleSubmit = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Get company_id from localStorage
    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (!selectedCompanyId) {
      error.value = 'Please select a company first';
      loading.value = false;
      return;
    }

    // Calculate totals from items
    const subtotal = itemsTotal.value;
    const grandTotal = subtotal; // For now, no tax calculation

    // Create FormData for file upload if logo exists
    const formDataToSend = new FormData();
    formDataToSend.append('type', formData.type);
    formDataToSend.append('company_id', selectedCompanyId);
    
    // Generate barcode data (JSON string with invoice info)
    const barcodeData = JSON.stringify({
      invoice_number: formData.invoice_number,
      date: formData.invoice_date,
      seller: formData.seller_name,
      total: grandTotal,
      currency: formData.currency || 'BGN'
    });

    const resultData = {
      invoice_number: formData.invoice_number,
      invoice_date: formData.invoice_date,
      due_date: formData.due_date || null,
      currency: formData.currency || 'BGN',
      seller_name: formData.seller_name,
      seller_tax_id: formData.seller_tax_id || null,
      seller_address: formData.seller_address || null,
      buyer_name: formData.buyer_name || null,
      buyer_email: formData.buyer_email || null,
      buyer_tax_id: formData.buyer_tax_id || null,
      buyer_address: formData.buyer_address || null,
      subtotal: subtotal.toFixed(2),
      tax_rate: '0',
      tax_amount: '0',
      grand_total: grandTotal.toFixed(2),
      notes: formData.notes || null,
      barcode: barcodeData,
      line_items: formData.items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: (item.quantity * item.unit_price).toFixed(2)
      }))
    };

    formDataToSend.append('result', JSON.stringify(resultData));
    
    // Add logo if uploaded
    if (logoFile.value) {
      formDataToSend.append('company_logo', logoFile.value);
    }

    const response = await api.post('/api/scans/manual', formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // Close modal and reload invoices
    showForm.value = false;
    await loadInvoices();
    
    // Optionally redirect to the new invoice details
    // router.push(`/scans/${response.data.id}`);
  } catch (err) {
    console.error('Failed to create manual invoice:', err);
    error.value = err.response?.data?.message || 'Failed to create invoice. Please try again.';
  } finally {
    loading.value = false;
  }
};

const handleBack = () => {
  router.back();
};

/* What each field offers, as a list: the fields are the app's own
   dropdowns now, so the options are data rather than tags. */
const typeOptions = computed(() => [
  { value: 'expense', label: '💸 Expense' },
  { value: 'income', label: '💰 Income' },
]);
const type2Options = computed(() => [
  { value: '', label: 'Select type...' },
  { value: 'income', label: 'Income (Sale)' },
  { value: 'expense', label: 'Expense (Purchase)' },
  { value: 'proforma', label: 'Proforma' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'debit_note', label: 'Debit Note' },
]);
</script>

<style scoped>
/* Animations */
@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-slide-up {
  animation: slide-up 0.4s ease-out forwards;
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out;
}

/* Utility Classes */
.grid { display: grid; }
.grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }
.mb-1 { margin-bottom: 0.25rem; }
.mb-4 { margin-bottom: 1rem; }
.mb-6 { margin-bottom: 1.5rem; }
.mb-8 { margin-bottom: 2rem; }
.p-5 { padding: 1.25rem; }
.py-16 { padding-top: 4rem; padding-bottom: 4rem; }
.w-5 { width: 1.25rem; }
.h-5 { height: 1.25rem; }
.w-10 { width: 2.5rem; }
.h-10 { height: 2.5rem; }
.w-20 { width: 5rem; }
.h-20 { height: 5rem; }
.rounded-lg { border-radius: 0.5rem; }
.rounded-xl { border-radius: 0.75rem; }
.rounded-full { border-radius: 9999px; }
.text-xs { font-size: 0.75rem; line-height: 1rem; }
.text-sm { font-size: 0.875rem; line-height: 1.25rem; }
.text-lg { font-size: 1.125rem; line-height: 1.75rem; }
.text-2xl { font-size: 1.5rem; line-height: 2rem; }
.font-bold { font-weight: 700; }
.font-semibold { font-weight: 600; }
.font-medium { font-weight: 500; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.bg-card { background-color: white; }
.bg-accent { background-color: #f1f5f9; }
.bg-muted { background-color: #f1f5f9; }
.bg-status-paid\/15 { background-color: rgba(16, 185, 129, 0.15); }
.text-accent-foreground { color: #64748b; }
.text-muted-foreground { color: #64748b; }
.text-foreground { color: #0f172a; }
.text-status-paid { color: #10b981; }
.text-primary-foreground { color: white; }
.gradient-primary { background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%); }
.invoice-shadow { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1); }

@media (min-width: 768px) {
  .md\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

/* Original Styles */
.manual-invoice-page {
  min-height: 100vh;
  background: var(--bg-gray);
  padding: 24px 0;
}

.page-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Stats Cards Animations */
@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-slide-up {
  animation: slide-up 0.4s ease-out forwards;
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out forwards;
}

/* Card Styles */
.bg-card {
  background: white;
}

.invoice-shadow {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.bg-accent {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.text-accent-foreground {
  color: white;
}

.text-muted-foreground {
  color: #64748b;
}

.text-foreground {
  color: #1e293b;
}

.bg-status-paid\/15 {
  background: rgba(34, 197, 94, 0.15);
}

.text-status-paid {
  color: #22c55e;
}

.gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.text-primary-foreground {
  color: white;
}

/* Button Styles */
.btn-new-invoice {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-new-invoice:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-create-first {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-create-first:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Invoice Table */
.invoices-table {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scan-row {
  display: grid;
  grid-template-columns: 40px 140px 80px 100px 1fr 240px;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.scan-row:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.scan-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.scan-status {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}

.status-completed {
  background: #dcfce7;
  color: #166534;
}

.scan-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--gray-dark);
}

.scan-thumbnail {
  width: 80px;
  height: 100px;
  background: var(--bg-gray);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.invoice-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.invoice-type {
  font-size: 11px;
  font-weight: 700;
  color: var(--gray-dark);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invoice-number {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.invoice-details {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--gray-dark);
}

.company-text {
  font-weight: 500;
}

.invoice-amount {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.amount-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.amount-currency {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-dark);
}

.invoice-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--gray-dark);
}

.invoice-type-selector {
  margin-top: 4px;
}

.type-select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.type-select:hover {
  border-color: #cbd5e1;
}

.type-select:focus {
  outline: none;
  border-color: var(--primary);
}

.scan-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-action svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
}

.btn-view {
  background: #0ea5e9;
  color: white;
}

.btn-view:hover {
  background: #0284c7;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-export {
  background: #10b981;
  color: white;
}

.btn-export:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-action.btn-delete {
  background: #ef4444;
  color: white;
}

.btn-action.btn-delete:hover {
  background: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
}

.modal-container {
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.btn-close-modal {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--gray-dark);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* Invoice Form Styles */
.invoice-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header-with-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

.form-field input,
.form-field select,
.form-field textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  transition: border-color 0.2s ease;
}

.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-field textarea {
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.upload-box {
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.upload-hint {
  font-size: 12px;
  color: #94a3b8;
}

/* Items Table */
.items-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 80px 120px 100px 40px;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 80px 120px 100px 40px;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row input {
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
}

.table-row input:focus {
  outline: none;
  border-color: #3b82f6;
}

.col-description { grid-column: 1; }
.col-qty { grid-column: 2; text-align: center; }
.col-price { grid-column: 3; }
.col-total { grid-column: 4; text-align: right; font-weight: 500; }
.col-action { grid-column: 5; text-align: center; }

.btn-add-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-remove-item {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-remove-item:hover {
  background: #fef2f2;
  color: #ef4444;
}

.total-amount {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 16px 12px;
  font-size: 16px;
}

.total-amount strong {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.section-header {
  margin: 32px 0 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border);
}

.section-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Logo Upload */
.logo-upload-container {
  width: 100%;
}

.logo-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  border: 2px dashed var(--border);
  border-radius: 12px;
  background: var(--bg-gray);
  transition: all 0.2s ease;
}

.logo-upload-placeholder:hover {
  border-color: var(--primary);
  background: rgba(102, 126, 234, 0.05);
}

.btn-upload-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-upload-logo:hover {
  background: var(--primary-dark);
  transform: translateY(-1px);
}

.upload-hint {
  margin-top: 12px;
  font-size: 13px;
  color: var(--gray-dark);
}

.logo-preview {
  position: relative;
  display: inline-block;
  max-width: 300px;
  padding: 16px;
  border: 2px solid var(--border);
  border-radius: 12px;
  background: white;
}

.logo-preview img {
  max-width: 100%;
  max-height: 200px;
  display: block;
  border-radius: 8px;
}

.btn-remove-logo {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-remove-logo:hover {
  background: #dc2626;
  transform: scale(1.1);
}

/* Total Input */
.total-label {
  font-weight: 700;
  color: var(--primary);
}

.total-input {
  font-weight: 700;
  font-size: 16px;
  background: var(--bg-gray);
  color: var(--primary);
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 20px;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 8px;
}

.btn {
  padding: 11px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
}

.btn-cancel {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: var(--text-primary);
}

.btn-create {
  background: #16a34a;
  color: white;
  min-width: 140px;
}

.btn-create:hover:not(:disabled) {
  background: #15803d;
}

.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}

/* The row actions inside the tables, drawn like the dashboard's: the mark in
   its own colour with nothing behind it, and the hover is a two-pixel lift.
   They were filled squares — blue, green and red — three per row. */
.btn-action,
.btn-action.btn-view,
.btn-action.btn-export,
.btn-action.btn-delete {
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    box-shadow: none;
}

.btn-action svg {
    width: 14px;
    height: 14px;
}

.btn-action.btn-view { color: #0697e4; }
.btn-action.btn-export { color: #0f9d6f; }
.btn-action.btn-delete { color: #e04545; }

.btn-action:hover,
.btn-action.btn-view:hover,
.btn-action.btn-export:hover,
.btn-action.btn-delete:hover {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    transform: translateY(-2px);
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* The row of answers under a question — "delete these seven?" — is a footer
   like any other: a hairline above it and the two buttons at its end. */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #dde3ea;
    background: #fff;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── The rest of the buttons ───────────────────────────────────────────
 *
 * The chrome above settles the ones named primary, secondary, cancel and
 * save; these carry names of their own and so kept the old 15px-in-a-49px-box
 * shape. Same geometry as the others: a 32px box, a 13px label, 8px corner,
 * and a filled face only where something is being committed. */
.btn-new-invoice,
.btn-create-first,
.btn-add-item,
.btn-close-modal,
.btn-remove-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
  transition: all 0.15s ease;
  width: auto;
}

.btn-new-invoice,
.btn-create-first {
  background: #0697e4;
  border: 1px solid #0697e4;
  color: #fff;
}

.btn-new-invoice:hover:not(:disabled),
.btn-create-first:hover:not(:disabled) {
  background: #0b84c7;
  border-color: #0b84c7;
  box-shadow: none;
  transform: none;
}

.btn-add-item,
.btn-close-modal {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #1e293b;
}

.btn-add-item:hover:not(:disabled),
.btn-close-modal:hover:not(:disabled) {
  background: #fff;
  border-color: #0697e4;
  color: #0697e4;
  box-shadow: none;
  transform: none;
}

.btn-remove-item {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #e04545;
}

.btn-remove-item:hover:not(:disabled) {
  background: #fff;
  border-color: #e04545;
  color: #e04545;
  box-shadow: none;
  transform: none;
}


/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── One frame, not two ────────────────────────────────────────────────
 *
 * The chrome above gives a hairline, a 12px corner and a shadow to both
 * .modal-content and .modal-container — but in this dialogue the container
 * is only the box that centres the card, and in one case it is far wider
 * than the card, so its edge was drawn as a rectangle standing in the empty
 * space beside the dialogue. The card keeps the frame; the wrapper carries
 * none. */
.modal-container {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
</style>
